import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Animal, AnimalsResponse } from "./types";

// Get the base URL from environment variables
const baseUrl = process.env.REACT_APP_BASE_URL;

// Define our API service
export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({ baseUrl: `${baseUrl}/api` }),
  tagTypes: ["Animals"],
  endpoints: (builder) => ({
    // Get all animals
    getAnimals: builder.query<AnimalsResponse, void>({
      query: () => "animals",
      providesTags: ["Animals"],
    }),

    // Get a single animal by ID
    getAnimal: builder.query<Animal, number>({
      query: (id) => `animals/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Animals", id }],
    }),

    // Create a new animal
    createAnimal: builder.mutation<
      Animal,
      { animal: Omit<Animal, "id">; files: FileList | null }
    >({
      query: ({ animal, files }) => {
        const formData = new FormData();
        formData.append("animal", JSON.stringify(animal));
        if (files) {
          Array.from(files).forEach((file) => {
            formData.append("pictures", file);
          });
        }
        return {
          url: "animals",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Animals"],
    }),

    // Update an animal
    updateAnimal: builder.mutation<Animal, Animal>({
      query: (animal) => ({
        url: `animals/${animal.id}`,
        method: "PUT",
        body: animal,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Animals", id }],
    }),

    // Delete an animal
    deleteAnimal: builder.mutation<void, string>({
      query: (id) => ({
        url: `animals/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Animals"],
    }),
  }),
});

// Export hooks for each endpoint
export const {
  useGetAnimalsQuery,
  useGetAnimalQuery,
  useCreateAnimalMutation,
  useUpdateAnimalMutation,
  useDeleteAnimalMutation,
} = api;
